import React from "react";
import { Building2, Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ServiceTitanWorkType } from "@/api/updateBusinessUnitWorkTypes/types";
import { CATCH_ALL_SERVICE_TITAN_ID } from "@/api/fetchBusinessUnits/types";

interface Trade {
  id: string;
  name: string;
}

/**
 * Radix rejects an empty-string item value, so "no work type" travels as a
 * sentinel and is translated back to null on the way out.
 */
const NO_WORK_TYPE = "unset";

/**
 * "Replacement / Install" rather than bare "Replacement": a shop that installs
 * a new system and one that replaces an old one are doing the same job, and
 * naming only one of them makes members with the other wonder where they fit.
 *
 * This is a ServiceTitan work type, not a ledger account. Average Replacement
 * Ticket reads it; the revenue split that once carried the same name is gone
 * from the CertainPath catalog and the two were never connected in code.
 */
const WORK_TYPE_LABELS: Record<ServiceTitanWorkType, string> = {
  service: "Service",
  replacement: "Replacement / Install",
};

interface BusinessUnitMatcherProps {
  mappingId: string;
  businessUnitId: string;
  /** ServiceTitan's own id for the unit. See `CATCH_ALL_SERVICE_TITAN_ID`. */
  serviceTitanId: number;
  businessUnitName: string;
  tradeId: string;
  trades: Trade[];
  onMappingChange: (
    mappingId: string,
    field: "businessUnitId" | "tradeId",
    value: string,
  ) => void;
  workType: ServiceTitanWorkType | null;
  onWorkTypeChange: (
    mappingId: string,
    businessUnitId: string,
    workType: ServiceTitanWorkType | null,
  ) => void;
  isLocked: boolean;
}

const BusinessUnitMatcher: React.FC<BusinessUnitMatcherProps> = ({
  mappingId,
  businessUnitId,
  serviceTitanId,
  businessUnitName,
  tradeId,
  trades,
  onMappingChange,
  workType,
  onWorkTypeChange,
  isLocked,
}) => {
  const isCatchAll = serviceTitanId === CATCH_ALL_SERVICE_TITAN_ID;

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <Building2 className="h-5 w-5 text-gray-400 dark:text-slate-500 mr-3" />
          <div className="text-sm font-medium text-gray-900 dark:text-slate-100">
            {businessUnitName}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {isLocked && (
            <span title="This business unit is locked and cannot be modified">
              <Lock className="h-4 w-4 text-gray-400 dark:text-slate-500" />
            </span>
          )}
          <Select
            disabled={isLocked}
            onValueChange={(value) =>
              onMappingChange(mappingId, "tradeId", value)
            }
            value={tradeId || undefined}
          >
            <SelectTrigger className="max-w-xs">
              <SelectValue placeholder="Choose trade..." />
            </SelectTrigger>
            <SelectContent>
              {trades.map((trade) => (
                <SelectItem key={trade.id} value={trade.id}>
                  {trade.name}
                </SelectItem>
              ))}
              <SelectItem value="do_not_import">Do not import</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </td>
      {/*
        Deliberately not disabled by `isLocked`. That lock stops a trade being
        changed after invoices have synced against it; a work type is read live
        when the dashboard renders, so there is no history to protect — and
        honouring the lock here would leave every member who has already saved
        their trade mappings permanently unable to set one.

        Saved on change rather than through "Save Mappings", for the same
        reason: that button's path locks rows and enforces trade rules this
        field has nothing to do with.

        **The catch-all unit gets no control at all.** It is the "Other" unit
        the sync auto-creates to hold jobs from the hidden and system-level
        units ServiceTitan does not return, so its work is a mix of service and
        replacement and there is no answer to ask the member for. The API
        refuses one (`CatchAllBusinessUnitWorkTypeException`, 400), and a
        dropdown whose every option raises an error toast is worse than no
        dropdown. Clearing is still offered when the row already carries a work
        type, because the API still accepts null there: a member who marked it
        before the rule existed would otherwise be stuck with a bucket counted
        in their KPIs and no way to undo it.
      */}
      <td className="px-6 py-4">
        {isCatchAll ? (
          <div className="flex max-w-xs flex-col items-start gap-1">
            <span className="text-sm text-gray-500 dark:text-slate-400">
              Not applicable — a catch-all for units ServiceTitan does not
              report, so its jobs are a mix.
            </span>
            {workType && (
              <button
                className="text-xs text-gray-500 underline underline-offset-2 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                onClick={() =>
                  onWorkTypeChange(mappingId, businessUnitId, null)
                }
                type="button"
              >
                Clear {WORK_TYPE_LABELS[workType]}
              </button>
            )}
          </div>
        ) : (
          <Select
            onValueChange={(value) =>
              onWorkTypeChange(
                mappingId,
                businessUnitId,
                value === NO_WORK_TYPE ? null : (value as ServiceTitanWorkType),
              )
            }
            value={workType ?? NO_WORK_TYPE}
          >
            <SelectTrigger className="max-w-xs">
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_WORK_TYPE}>Not set</SelectItem>
              {(Object.keys(WORK_TYPE_LABELS) as ServiceTitanWorkType[]).map(
                (value) => (
                  <SelectItem key={value} value={value}>
                    {WORK_TYPE_LABELS[value]}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        )}
      </td>
    </tr>
  );
};

export default BusinessUnitMatcher;
